import { useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { AgoraPlanNameE } from '@/types/cyclone/models';
import { isInternationalAgoraUp } from '@/utils/agora-up-market';

// Usernames with access to beta features
const BETA_USERNAMES = ['lodejuana', 'demo-marketing', 'florgonzalez', 'lucianamartinez'];

// Per-vendor UI overrides — venues that asked us to hide specific surfaces.
// Comisiones / revenue-share UI hidden for these usernames.
const HIDE_REVENUE_SHARE_USERNAMES = ['cofashion'];
// Staff access + permissions UI hidden for these usernames.
const HIDE_STAFF_ACCESS_USERNAMES = ['cofashion'];
// Render the Equipo navbar entry as a flat "Recursos" link (no dropdown) for these usernames.
const RESOURCES_NAV_USERNAMES = ['cofashion'];
// "Actividad landing" — consolidated read-only activity of the associated landing
// professionals. Only the Cofashion owner sees this extra tab.
const LANDING_ACTIVITY_USERNAMES = ['cofashion'];

export const useFeatureFlags = () => {
  const { session } = useAuth();

  const flags = useMemo(() => {
    const username = session?.vendor?.username;
    const isGodMode = !!localStorage.getItem('vendorUsernameGodMode');
    const hasBetaAccess = isGodMode || (username ? BETA_USERNAMES.includes(username) : false);
    // Clientes con segmentos (vista nueva, barra con IA, exportación por formato, etiquetas en
    // grupo): disponible para todos desde el lanzamiento, ya no hay beta.
    const clientSegmentsPreview = true;
    const isVenue = !!session?.is_venue;
    const hideRevenueShare = !!username && HIDE_REVENUE_SHARE_USERNAMES.includes(username);
    const hideStaffAccess = !!username && HIDE_STAFF_ACCESS_USERNAMES.includes(username);
    const useResourcesNav = !!username && RESOURCES_NAV_USERNAMES.includes(username);
    // Catálogo de Productos: la beta cerró — la sección es visible para TODOS (GA), así que
    // cualquier vendor puede descubrir el add-on y activarlo desde Facturación.
    // Ojo: esto es solo VISIBILIDAD. El add-on sigue siendo pago y el acceso real lo sigue
    // gateando el flag de plan catalog_products_enabled (CATALOG_PRODUCTS_ACTIVE), tanto acá
    // como en el backend (product.repo + manual-product-sale).
    const hasProductsAccess = true;
    // Fidelización: también por flag de vendor (toggle vía MCP/Alfred).
    const hasLoyaltyAccess = isGodMode || session?.vendor?.loyalty_enabled === true;
    // Premios (v2 de Fidelización): cohorte de la beta cerrada, flag por vendor (SQL/MCP).
    // El staff hereda el flag del dueño (lo expone la sesión).
    const hasPremiosAccess = isGodMode || session?.vendor?.premios_beta_enabled === true;
    // Mercado Pago por profesional: cohorte de la beta, flag por vendor (SQL/MCP). Sin god-mode:
    // las pantallas nuevas llaman endpoints que el backend cierra si el negocio no tiene el flag.
    const hasMpPorProfesional = session?.vendor?.mp_por_profesional_beta_enabled === true;
    // Conversaciones (beta): bandeja de la vidriera y WhatsApp. Flag por vendor
    // (`conversations_beta_enabled`, el staff lo hereda del dueño); el profesional del equipo
    // entra sólo con `conversations_view`. Modo dios entra siempre, como en cyclone.
    const hasConversations =
      isGodMode ||
      (session?.vendor?.conversations_beta_enabled === true &&
        (!session?.is_staff || session.vendor?.vendor_permissions?.conversations_view === true));
    // Disponible para todos desde el lanzamiento: ya no hay beta por cohorte.
    const hasOffersAccess = true;
    // Disponible para todos; pedir y mostrar reseñas de Ágora requiere opt-in en Ajustes.
    const hasReviewsAccess = true;
    const hasLandingActivity = !!username && LANDING_ACTIVITY_USERNAMES.includes(username);
    // Gestión de Caja: se habilita por negocio desde el backend (vendor.cash_register_enabled),
    // toggle vía MCP/Alfred. El staff hereda el flag del dueño (lo expone la sesión).
    const cashRegisterFlag = session?.vendor?.cash_register_enabled === true;
    // Reservas recurrentes, densidad y vista Mes de la agenda están disponibles para todos.
    const hasRecurringBookingsAccess = true;
    const hasAgendaDensityAccess = true;
    const hasAgendaMonthViewAccess = true;
    // El flag beta sólo controla descubrimiento/rollout. El acceso real lo da
    // el add-on activo o un plan que ya incluye Finanzas Pro.
    const financeAddOnActive = session?.active_plan?.revenue_share_enabled === true;
    const planName = session?.vendor?.plan_name;
    // Buzón de Ideas: sigue abriéndose vendor por vendor (`idea_box_enabled`, toggle vía
    // MCP/Alfred) y además queda incluido en Ágora UP — todo negocio de ese plan lo tiene
    // sin que haya que prenderle el flag a mano. Espejo del gate de cyclone
    // (`src/middlewares/idea_box_access.ts`): si cambia allá, cambiar acá.
    const hasIdeaBoxAccess =
      isGodMode || session?.vendor?.idea_box_enabled === true || planName === AgoraPlanNameE.AGORA_UP;
    const financialToolsIncluded =
      planName === AgoraPlanNameE.OPERATOR ||
      planName === AgoraPlanNameE.AGORA_UP ||
      planName === AgoraPlanNameE.CAR;
    // Plan Car (Módulo Taller): el gate es el plan DEL NEGOCIO (`business_plan_name`, resuelto en
    // cyclone sobre el dueño) porque el profesional del equipo figura siempre 'professional'.
    // Todo lo exclusivo de Car (matriz de precios, listo para retirar, foto del cliente) cuelga
    // de este flag y NO aparece en los demás planes, ni bloqueado ni con upsell.
    const isCarPlan = (session?.business_plan_name ?? planName) === AgoraPlanNameE.CAR;
    // El entitlement DEL NEGOCIO llega resuelto desde cyclone. Es la única fuente válida
    // para un profesional del equipo: no tiene fila en `vendor_agora_plans` y su `plan_name`
    // es siempre 'professional', así que derivarlo de su propia sesión le apagaría Finanzas
    // al equipo de todo negocio que sí lo contrató.
    const businessFinanzasEntitlement = session?.business_finanzas_entitlement === true;
    const hasFinanzasEntitlement = financeAddOnActive || financialToolsIncluded;
    // Comisiones no se deriva de `is_venue` solamente. Entran el plan venue legacy
    // (`professional` + dueño de venue) y Ágora UP, que vende hasta cuatro
    // profesionales en la base: un negocio con equipo ya lo tiene incluido.
    // Operator/Plan Negocio también puede tener equipo pero su habilitación sigue
    // siendo comercial y se decide aparte. Espejo de `isRevenueShareVenuePlan` en
    // cyclone (`src/services/revenue-share/access.ts`): si cambia allá, cambiar acá.
    const hasRevenueShareVenuePlan =
      isVenue &&
      (planName === AgoraPlanNameE.PROFESSIONAL ||
        planName === AgoraPlanNameE.AGORA_UP ||
        planName === AgoraPlanNameE.CAR);
    // Lo comercial no es sólo el add-on: Ágora UP incluye Finanzas Pro en el precio
    // base y nunca prende `revenue_share_enabled`.
    const revenueShareActive =
      hasRevenueShareVenuePlan &&
      (financeAddOnActive || financialToolsIncluded || businessFinanzasEntitlement) &&
      !hideRevenueShare;
    const catalogProductsActive = session?.active_plan?.catalog_products_enabled === true;
    // Comunidad en beta: God Mode para el equipo, y community_beta_enabled para
    // los negocios invitados a la cohorte (se prende por MCP, vendor por vendor).
    // El plan es un segundo gate: define si el add-on ya fue activado.
    const hasCommunityAccess = isGodMode || session?.vendor?.community_beta_enabled === true;
    const communityActive = hasCommunityAccess && session?.active_plan?.community_enabled === true;

    // Fichas de clientes: beta cerrada por cohorte — el flag viaja en session.vendor
    // (columna vendors.client_forms_beta_enabled, se prende por SQL sin deploy).
    const hasClientFormsAccess = isGodMode || session?.vendor?.client_forms_beta_enabled === true;

    // Referidos (vista nueva: link, crédito y referidos): para todos desde el 29/10/2026, ya no hay beta.
    const hasReferralsV2 = true;

    // Restringir reservas online de un cliente (lista negra): para todos desde el 28/10/2026, la
    // columna vendors.booking_restrictions_beta_enabled quedó en true por defecto y sólo sirve
    // para apagársela a un negocio puntual.
    const hasBookingRestrictions = isGodMode || session?.vendor?.booking_restrictions_beta_enabled === true;
    // Tu mes en historias (Contenido IG): beta por negocio y sólo el dueño. El equipo no ve
    // historias, ni en modo dios (espejo del controller de cyclone, que le responde 404).
    const hasHistorias =
      (isGodMode || session?.vendor?.historias_beta_enabled === true) && !session?.is_staff;

    // Volver a reservar (recordatorio a clientes que hace tiempo no vienen): beta por negocio,
    // columna vendors.rebooking_beta_enabled (SQL o MCP `vendor-toggle-volver-a-reservar`); el staff
    // hereda la del dueño. Además es de Pro (candado con `isProMessageType`). Espejo de
    // `rebookingProDenial` y del envío diario en cyclone.
    const hasRebooking = isGodMode || session?.vendor?.rebooking_beta_enabled === true;

    // Finanzas Pro se comporta como el add-on de Catálogo: la sección es VISIBLE para todo
    // dueño, así que cualquiera puede descubrirla y contratarla; el acceso real lo gatea el
    // entitlement (FINANZAS_ACTIVE) acá y `requireFinanzasBeta` en el backend. Sin contratar,
    // las rutas caen en el onboarding del add-on en vez de desaparecer del rail.
    // El equipo entra SOLO con el permiso `finanzas_view` y SOLO si el negocio ya tiene el
    // add-on: a diferencia del dueño, al profesional no se le ofrece descubrirlo — no es
    // suyo para contratar, y una sección bloqueada que lleva a Facturación sería un
    // callejón sin salida. Además entra en modo lectura (ver `useFinanzasReadOnly`).
    const staffFinanzasView =
      session?.is_staff === true && session?.vendor?.vendor_permissions?.finanzas_view === true;
    const hasFinanzasAccess = session?.is_staff ? staffFinanzasView && businessFinanzasEntitlement : true;
    // El entitlement NO se saltea con god-mode: impersonar tiene que mostrar lo que el
    // negocio ve de verdad. Con el bypass era imposible probar el paywall — parecía que
    // un negocio dado de baja seguía teniendo acceso.
    const finanzasActive = session?.is_staff ? hasFinanzasAccess : hasFinanzasEntitlement;
    // Quién ve la CARD del add-on en Facturación: no la ofrecemos a quien ya lo tiene
    // incluido en el plan (ahí no se contrata ni se da de baja).
    const hasFinanzasAvailable = hasFinanzasAccess && !financialToolsIncluded && !session?.is_staff;
    // Pro (antes «Finanzas Pro»): mismo entitlement que Finanzas, pero como gate de TODO lo
    // que trae (Finanzas, fichaje, Analítica › Marketing, volver a reservar). Para el equipo
    // es el del negocio y no depende del permiso `finanzas_view`: un profesional sin ese
    // permiso igual usa lo de Pro que no es plata. Como Finanzas, god mode NO lo saltea.
    const proActive = session?.is_staff ? businessFinanzasEntitlement : hasFinanzasEntitlement;
    // Caja es una sección más de Finanzas Pro: se MUESTRA a todo dueño (si no, no habría
    // cómo descubrirla) y sin contratar el add-on lleva al onboarding, igual que Gastos y
    // Resultado. `cash_register_enabled` sigue siendo el gate FUNCIONAL de ops — contratar
    // el add-on lo prende solo, y darlo de baja lo apaga.
    // Caja queda afuera del permiso de lectura: es una pantalla de OPERACIÓN (apertura,
    // cierre, movimientos), no una de consulta. Sigue siendo del dueño.
    const hasCashRegisterAvailable = hasFinanzasAccess && !session?.is_staff;
    const hasCashRegisterAccess = hasFinanzasEntitlement && cashRegisterFlag;
    // Analítica v2: disponible para todos desde el lanzamiento, ya no hay beta.
    const hasAnalyticsV2 = true;
    return {
      UP_INTERNATIONAL: session?.up_international ?? isInternationalAgoraUp(session?.vendor),
      AI_CONTENT_GENERATION: true,
      // Equipo: `agenda_edit` es legado y la API lo deriva de cualquier escritura en la agenda
      // (reservas, bloqueos u horarios de un día). Es el único gate que lo sigue leyendo.
      AVAILABILITY_SHARE:
        hasBetaAccess &&
        (!session?.is_staff ||
          (!!session.vendor?.vendor_permissions?.agenda_view &&
            !!session.vendor?.vendor_permissions?.agenda_edit)),
      GOOGLE_BUSINESS: true,
      REVENUE_SHARE: hasRevenueShareVenuePlan && !hideRevenueShare,
      REVENUE_SHARE_ACTIVE: revenueShareActive,
      HIDE_STAFF_ACCESS: hideStaffAccess,
      USE_RESOURCES_NAV: useResourcesNav,
      GALLERY: true,
      LANDING_ACTIVITY: hasLandingActivity,
      CASH_REGISTER: hasCashRegisterAccess,
      CASH_REGISTER_AVAILABLE: hasCashRegisterAvailable,
      RECURRING_BOOKINGS: hasRecurringBookingsAccess,
      AGENDA_DENSITY: hasAgendaDensityAccess,
      AGENDA_MONTH_VIEW: hasAgendaMonthViewAccess,
      DYNAMIC_PRICING: true,
      // Programa de Fidelización — flag por vendor (toggle vía MCP/Alfred) o god-mode.
      LOYALTY: hasLoyaltyAccess,
      // Premios — reemplaza a Fidelización en el menú para la cohorte de la beta.
      PREMIOS: hasPremiosAccess,
      // Conversaciones (beta): bandeja, canales, plantillas y respuestas automáticas.
      CONVERSATIONS: hasConversations,
      // Mercado Pago por profesional: Medios de cobro renovados, «Turnos con Mercado Pago» y la
      // cuenta propia de cada profesional (ficha, tarjeta de Equipo, enlace para conectarla).
      MP_POR_PROFESIONAL: hasMpPorProfesional,
      VENDOR_ASSIGN_ADDONS: true,
      MESSAGES_TEMPLATES: true,
      PRODUCTS: hasProductsAccess,
      CATALOG_PRODUCTS_ACTIVE: catalogProductsActive,
      COMMUNITY: hasCommunityAccess,
      COMMUNITY_ACTIVE: communityActive,
      CLIENT_FORMS: hasClientFormsAccess,
      // La ficha V2 se abre cuando el catálogo del negocio está MIGRADO y habilitado
      // (session.client_fields_enabled, que sale de client_field_catalogs.enabled), no por
      // estar en la cohorte beta ni por una lista de ids compilada en el build: migrar un
      // negocio le prende el panel sin recompilar Flash. Pedir la cohorte además del catálogo
      // evitaría abrir el panel a un negocio migrado cuya API sigue cerrada por el gate beta.
      // La env queda sólo como corte global.
      CLIENT_FIELDS_V2:
        hasClientFormsAccess &&
        session?.client_fields_enabled === true &&
        import.meta.env.VITE_CLIENT_FIELDS_V2_ENABLED === 'true',
      FINANZAS: hasFinanzasAccess,
      FINANZAS_ACTIVE: finanzasActive,
      FINANZAS_AVAILABLE: hasFinanzasAvailable,
      PRO_ACTIVE: proActive,
      IDEA_BOX: hasIdeaBoxAccess,
      OFFERS: hasOffersAccess,
      // GA: visibilidad del módulo, no habilita solicitudes automáticas.
      REVIEWS: hasReviewsAccess,
      // Cobros parciales sobre una reserva: registrar N pagos contra el saldo, cada uno con
      // su medio + impacto en caja. GA para todos los vendors — va de la mano del modal de
      // cobro unificado (un solo CTA "Registrar cobro" en el detalle de reserva).
      PARTIAL_COLLECTIONS: true,
      CLIENT_SEGMENTS: clientSegmentsPreview,
      // Frost Tiers — liquid-glass adaptativo. Con el flag ON, el resolver de
      // device elige tier (2/3) y prende blur en las superficies capaces; OFF
      // deja todo en Tier 1 (look plano de hoy, cero regresión). Beta/god-mode
      // mientras se calibran umbrales; abrir por vendor cuando esté validado.
      FROST_TIERS: hasBetaAccess,
      CAR: isCarPlan,
      ANALYTICS_V2: hasAnalyticsV2,
      // Fichaje del equipo (Equipo › Asistencia): para todos desde el lanzamiento, sin beta. Es
      // de Pro y lee el MISMO gate que el resto de Pro, así el rail no muestra Asistencia abierta y
      // Marketing bloqueado (o al revés). Espejo de `requireTeamAttendanceBeta` en cyclone.
      TEAM_ATTENDANCE: proActive,
      REFERRALS_V2: hasReferralsV2,
      BOOKING_RESTRICTIONS: hasBookingRestrictions,
      TU_MES: hasHistorias,
      REBOOKING: hasRebooking,
      IS_GOD_MODE: isGodMode
    };
  }, [
    session?.vendor?.username,
    session?.vendor?.idea_box_enabled,
    session?.vendor?.client_forms_beta_enabled,
    session?.vendor?.community_beta_enabled,
    session?.vendor?.id,
    session?.vendor?.venue?.vendor_owner_id,
    session?.is_staff,
    session?.client_fields_enabled,
    session?.business_finanzas_entitlement,
    session?.business_plan_name,
    session?.vendor?.vendor_permissions?.finanzas_view,
    session?.vendor?.vendor_permissions?.agenda_view,
    session?.vendor?.vendor_permissions?.agenda_edit,
    session?.vendor?.cash_register_enabled,
    session?.vendor?.loyalty_enabled,
    session?.vendor?.premios_beta_enabled,
    session?.vendor?.mp_por_profesional_beta_enabled,
    session?.vendor?.booking_restrictions_beta_enabled,
    session?.vendor?.historias_beta_enabled,
    session?.vendor?.rebooking_beta_enabled,
    session?.vendor?.plan_name,
    session?.is_venue,
    session?.active_plan?.revenue_share_enabled,
    session?.active_plan?.catalog_products_enabled,
    session?.active_plan?.community_enabled
  ]);

  return flags;
};
